import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import type {
  MediaItem,
  RoomActivity,
  RoomChatMessage,
  RoomMember,
  RoomModAction,
  RoomPage,
  RoomRefusal,
  RoomRegionState,
  RoomRole,
  RoomStageState,
  View,
} from "@gooner/shared";
import { ROOM_ACTIVITY_HISTORY, ROOM_CHAT_HISTORY, ROOM_STAGE_PAGES } from "@gooner/shared";
import { registerSuper, track } from "../../analytics";
import { connectRoom, type RoomConn } from "../../lib/ws";
import { SyncBus } from "../syncBus";

export type RoomJoinStatus = "joining" | "open" | "closed" | "kicked" | "refused";

/** A scroll with no pages yet — what a member holds until the host's first page lands. */
export const EMPTY_STAGE: RoomStageState = { kind: "infinity", pages: [], active: 0 };

/** A line of the room's own among the chat: "x joined", "x left", a pick the room refused. */
export interface RoomNote {
  id: string;
  at: number;
  text: string;
}

/** The roster as people: one entry per username, this tab's own socket preferred. */
export function peopleOf(members: RoomMember[], you: string | null): RoomMember[] {
  const byName = new Map<string, RoomMember>();
  for (const m of members) {
    const cur = byName.get(m.username);
    if (!cur || m.pid === you) byName.set(m.username, cur && m.pid !== you ? cur : { ...m, cam: m.cam || (cur?.cam ?? false) });
  }
  return [...byName.values()];
}

export interface RoomCtx {
  roomId: string;
  /** The room's name and whether it is listed — for the chat's header and its copy-link. */
  name: string | null;
  isPublic: boolean;
  /** A house room: nobody hosts it. */
  house: boolean;
  /** A house room with its chat switched off: the frame draws no chat at all. */
  chatOff: boolean;
  connected: boolean;
  status: RoomJoinStatus;
  /** My socket's handle in this room. */
  you: string | null;
  role: RoomRole | null;
  /** ONE PER PERSON: the same account in two tabs is one face and one watcher. Of a
   *  person's sockets the entry kept is this tab's own where it is theirs, so `you`
   *  still names a row here. */
  members: RoomMember[];
  /** Everyone watching: the faces above plus the signed-out viewers, who have none. */
  watching: number;
  /** This tab signed in. A SIGNED-OUT viewer (2026-09-19) joins and counts, follows the
   *  host's screen, sees the chat move with its words veiled, and has no verbs. */
  signedIn: boolean;
  messages: RoomChatMessage[];
  /** The room's own lines — "x joined", "x left" — derived here from roster changes
   *  (never from the server, never one per socket), drawn grey among the messages. */
  notes: RoomNote[];
  /** What people did — the hub's grey lines with an actor: a session, an edge, a crown,
   *  the host's re-point or pause, a kick. A repeat comes back under the same id. */
  activity: RoomActivity[];
  /** Why the door stayed shut, or why I was put out. */
  refusal: { reason: RoomRefusal; message: string } | null;
  kickedReason: "kicked" | "banned" | "ended" | null;
  /** A refusal of a FRAME (rate, too long) — the composer shows it and it clears on the
   *  next send. Door refusals go to `refusal`. */
  frameError: string | null;
  /** THE SYNC: what the host's regions play, keyed by region id — a member's driven
   *  tiles subscribe to it exactly as a synced follower subscribes to its leader. */
  regionsBus: SyncBus<RoomRegionState>;
  /** The host has the wall paused (members hold; the host's own screen is its own). */
  paused: boolean;
  /** THE MIRROR: what the host is showing — the host's own id for the host, a member's
   *  masked one (a scroll's id, else the room's) — null while nothing is. The host's
   *  DisplayApp runs it as its viewId; a member's derives the stage from it. */
  showing: string | null;
  /** THE MIRROR, on a scroll: the host's pages and the `no` on their glass. A member's
   *  InfinityStage draws these instead of dealing its own. */
  stage: RoomStageState;
  /** Drop a grey line of the room's own into the chat — the screen's word on a pick the
   *  room could not take. */
  note: (text: string) => void;
  sendChat: (body: string) => void;
  setCam: (on: boolean) => void;
  mod: (action: RoomModAction, username: string) => void;
  sendFap: (regionId: string) => void;
  /** Owner only: my tile revealed a clip / I paused. The server refuses a member's. */
  sendReveal: (regionId: string, item: MediaItem, src: string) => void;
  sendPause: (paused: boolean) => void;
  /** My screen's own acts the room cannot see: a goon session started or ended, an edge. */
  sendAct: (kind: "session_start" | "session_end" | "edge", minutes?: number) => void;
  /** Owner only, THE MIRROR: my screen's current view; a page my scroll dealt; the page
   *  on my glass. Not queued — the host re-sends what it has on reconnect. */
  sendView: (view: View) => void;
  sendPage: (page: RoomPage) => void;
  sendActive: (active: number) => void;
  /** Somebody else in the room fapped a region: the stage throws the burst there. */
  onFap: (fn: (regionId: string, username?: string) => void) => () => void;
}

/**
 * One room socket for the life of the stage — the presence hook's shape: opened once,
 * handlers held in refs, verbs stable. `null` off a room. Signed out, no socket opens
 * at all (the server would only refuse it): the status reads `refused: signin` and the
 * stage asks for an account.
 */
export function useRoom(roomId: string | null, signedIn: boolean): RoomCtx | null {
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showing, setShowing] = useState<string | null>(null);
  const [stage, setStage] = useState<RoomStageState>(EMPTY_STAGE);
  // One bus for the hook's life; a new room's state re-seeds it (below), so its identity
  // can stay put and the stage's subscriptions with it.
  const busRef = useRef<SyncBus<RoomRegionState> | null>(null);
  if (!busRef.current) busRef.current = new SyncBus<RoomRegionState>();
  const fapListeners = useRef(new Set<(regionId: string, username?: string) => void>());
  const [status, setStatus] = useState<RoomJoinStatus>("joining");
  const [you, setYou] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [isPublic, setIsPublic] = useState(false);
  const [house, setHouse] = useState(false);
  const [chatOff, setChatOff] = useState(false);
  const [role, setRole] = useState<RoomRole | null>(null);
  const [rawMembers, setMembers] = useState<RoomMember[]>([]);
  const [watching, setWatching] = useState(0);
  const [messages, setMessages] = useState<RoomChatMessage[]>([]);
  const [notes, setNotes] = useState<RoomNote[]>([]);
  const [activity, setActivity] = useState<RoomActivity[]>([]);
  // Who was here last time we looked, by name — the diff is the join/leave lines. Null
  // until the first state: the people already in the room when you arrive are not news.
  const namesRef = useRef<Set<string> | null>(null);
  const [refusal, setRefusal] = useState<{ reason: RoomRefusal; message: string } | null>(null);
  const [kickedReason, setKickedReason] = useState<"kicked" | "banned" | "ended" | null>(null);
  const [frameError, setFrameError] = useState<string | null>(null);
  const connRef = useRef<RoomConn | null>(null);
  const joinedRef = useRef(false);
  const joinedAtRef = useRef<number | null>(null);
  const roleRef = useRef<RoomRole | null>(null);

  useEffect(() => {
    setStatus("joining");
    setYou(null);
    setRole(null);
    setMembers([]);
    setWatching(0);
    setMessages([]);
    setNotes([]);
    setActivity([]);
    namesRef.current = null;
    setRefusal(null);
    setKickedReason(null);
    setFrameError(null);
    setPaused(false);
    setShowing(null);
    setStage(EMPTY_STAGE);
    joinedRef.current = false;
    const bus = busRef.current!;
    for (const id of bus.ids()) bus.clear(id);
    if (!roomId) return;
    // Signed out, the socket opens all the same: the hub takes a viewer with no token as
    // a WATCHER — counted, following, no face, no verbs. The token (or its absence)
    // rides the hello; the hook re-runs on a sign-in so the same tab comes back as a person.
    const conn = connectRoom(roomId, {
      onStatus: setConnected,
      onState: (s) => {
        setName(s.name);
        setIsPublic(s.public);
        setHouse(s.house);
        setChatOff(s.chatOff);
        setYou(s.you);
        setRole(s.role);
        setMembers(s.members);
        setWatching(s.watching);
        setPaused(s.paused);
        setShowing(s.showing);
        setActivity(s.activity);
        // A state is the truth about the stage too: a re-point hands an empty one, so a
        // member's old pages are not drawn under the new showing.
        setStage(s.stage ?? EMPTY_STAGE);
        // The host's regions, whole: a state is the truth about every tile, so what the
        // bus held that the state does not name has ended (a re-point cleared it).
        const named = new Set(Object.keys(s.regions));
        for (const id of bus.ids()) if (!named.has(id)) bus.clear(id);
        for (const [id, st] of Object.entries(s.regions)) bus.publish(id, st);
        // A rejoin's tail is merged, not replaced: what this tab saw meanwhile stays.
        setMessages((cur) => {
          const seen = new Set(cur.map((m) => m.id));
          const merged = [...cur, ...s.messages.filter((m) => !seen.has(m.id))].sort((a, b) => a.at - b.at);
          return merged.slice(-ROOM_CHAT_HISTORY);
        });
        setStatus("open");
        if (!joinedRef.current) {
          joinedRef.current = true;
          joinedAtRef.current = Date.now();
          roleRef.current = s.role;
          track("room_joined", { role: s.role, house: s.house });
          // From here every event this tab sends says which side of the room it is on.
          registerSuper({ room_role: s.role === "owner" ? "host" : "member" });
        }
      },
      onRoster: (members, count) => {
        setMembers(members);
        setWatching(count);
      },
      onPlaying: (regionId, state) => bus.publish(regionId, state),
      onPause: setPaused,
      onShowing: (viewId) => {
        setShowing((cur) => {
          // The first showing is the room's answer at the door; a change is a re-point.
          if (cur !== null && cur !== viewId) track("room_repointed", { role: roleRef.current === "owner" ? "host" : "member" });
          return viewId;
        });
      },
      onActivity: (a) =>
        setActivity((cur) => {
          const next = cur.some((x) => x.id === a.id) ? cur.map((x) => (x.id === a.id ? a : x)) : [...cur, a];
          return next.length > ROOM_ACTIVITY_HISTORY ? next.slice(-ROOM_ACTIVITY_HISTORY) : next;
        }),
      onPage: (page) =>
        setStage((cur) => {
          const pages = [...cur.pages.filter((p) => p.no !== page.no), page].sort((a, b) => a.no - b.no).slice(-ROOM_STAGE_PAGES);
          return { ...cur, pages };
        }),
      onActive: (active) => setStage((cur) => (cur.active === active ? cur : { ...cur, active })),
      onFap: (regionId, username) => {
        for (const fn of fapListeners.current) fn(regionId, username);
      },
      onMessage: (m) =>
        setMessages((cur) => {
          if (cur.some((x) => x.id === m.id)) return cur;
          const next = [...cur, m];
          return next.length > ROOM_CHAT_HISTORY ? next.slice(-ROOM_CHAT_HISTORY) : next;
        }),
      onKicked: (reason) => {
        setKickedReason(reason);
        setStatus("kicked");
      },
      onClosed: () => setStatus("closed"),
      onRefused: (reason, message) => {
        if (reason === "signin" || reason === "banned" || reason === "no_room" || reason === "full") {
          setRefusal({ reason, message });
          setStatus("refused");
          track("room_join_failed", { reason });
        } else {
          setFrameError(message);
        }
      },
    });
    connRef.current = conn;
    return () => {
      conn.close();
      connRef.current = null;
      if (joinedAtRef.current !== null) {
        track("room_left", { role: roleRef.current === "owner" ? "host" : "member", durationSec: Math.round((Date.now() - joinedAtRef.current) / 1000) });
        joinedAtRef.current = null;
      }
      registerSuper({ room_role: null });
    };
  }, [roomId, signedIn]);

  const members = useMemo(() => peopleOf(rawMembers, you), [rawMembers, you]);
  useEffect(() => {
    if (status !== "open") return;
    const now = new Set(members.map((m) => m.username));
    const before = namesRef.current;
    namesRef.current = now;
    if (!before) return;
    const lines: RoomNote[] = [];
    const at = Date.now();
    for (const n of now) if (!before.has(n)) lines.push({ id: `note:${at}:${n}:in`, at, text: `${n} joined` });
    for (const n of before) if (!now.has(n)) lines.push({ id: `note:${at}:${n}:out`, at, text: `${n} left` });
    if (lines.length) setNotes((cur) => [...cur, ...lines].slice(-ROOM_CHAT_HISTORY));
  }, [members, status]);

  const note = useCallback((text: string) => {
    const at = Date.now();
    setNotes((cur) => [...cur, { id: `note:${at}:${Math.random()}`, at, text }].slice(-ROOM_CHAT_HISTORY));
  }, []);
  const sendChat = useCallback((body: string) => {
    setFrameError(null);
    connRef.current?.sendChat(body);
    track("room_message_sent", { kind: "text" });
  }, []);
  const setCam = useCallback((on: boolean) => connRef.current?.setCam(on), []);
  const mod = useCallback((action: RoomModAction, username: string) => {
    connRef.current?.mod(action, username);
    track("room_moderated", { action });
  }, []);
  const sendFap = useCallback((regionId: string) => connRef.current?.sendFap(regionId), []);
  const sendReveal = useCallback((regionId: string, item: MediaItem, src: string) => connRef.current?.sendReveal(regionId, item, src), []);
  const sendPause = useCallback((p: boolean) => connRef.current?.sendPause(p), []);
  const sendView = useCallback((v: View) => connRef.current?.sendView(v), []);
  const sendAct = useCallback((kind: "session_start" | "session_end" | "edge", minutes?: number) => connRef.current?.sendAct(kind, minutes), []);
  const sendPage = useCallback((p: RoomPage) => connRef.current?.sendPage(p), []);
  const sendActive = useCallback((n: number) => connRef.current?.sendActive(n), []);
  const onFap = useCallback((fn: (regionId: string, username?: string) => void) => {
    fapListeners.current.add(fn);
    return () => {
      fapListeners.current.delete(fn);
    };
  }, []);

  if (!roomId) return null;
  return {
    roomId, name, isPublic, house, chatOff, connected, status, you, role, members, watching, signedIn, messages, notes, activity, refusal, kickedReason, frameError,
    regionsBus: busRef.current!, paused, showing, stage,
    note, sendChat, setCam, mod, sendFap, sendReveal, sendPause, sendAct, sendView, sendPage, sendActive, onFap,
  };
}
